import React, { forwardRef, useCallback, useMemo } from 'react'
import { RadioGroup as MUIRadioGroup, Radio, FormControlLabel, FormHelperText, FormControl } from '@mui/material'
import { useField, useFormikContext } from 'formik'

interface RadioGroupProps {
  name: string
  options: { label: string; value: string | number }[]
  onChange?: (value: string | number) => void
}

const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(({ name, options, onChange, ...props }, ref) => {
  const { setFieldValue } = useFormikContext()
  const [field, meta] = useField(name)

  const hasError = useMemo(() => Boolean(meta.error && (meta.touched || meta.initialValue !== meta.value)), [meta])

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      setFieldValue(name, value)
      if (onChange) {
        onChange(value)
      }
    },
    [name, onChange, setFieldValue]
  )

  return (
    <FormControl component="fieldset" error={hasError}>
      <MUIRadioGroup {...field} {...props} ref={ref} onChange={handleChange}>
        {options.map(option => (
          <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={option.label} />
        ))}
      </MUIRadioGroup>
      {hasError && <FormHelperText>{meta.error}</FormHelperText>}
    </FormControl>
  )
})

RadioGroup.displayName = 'RadioGroup'

export default RadioGroup
